import React, { useEffect, useRef, useState } from 'react';
import { marked } from 'marked';
import ExplanationModal from './ExplanationModal.jsx';
import { trackEvent, getApiErrorMessage } from '../firebase/config.js';
import { Events } from '../firebase/events.js';
import { startAttempt, submitAnswer, finishAttempt, reportQuestion } from '../lib/exams.js';
import { normalizeReferences } from './clinical/clinicalCardUtils.js';

const SECONDS_PER_QUESTION = 90;

// Parses USMLE-style question text into structured vignette sections.
function parseVignette(text = '') {
    if (!text || text.length < 80) return null;
    const plain = text.replace(/<[^>]+>/g, '');
    const agePt = plain.match(/^(A|An)\s+(\d+[-–]?(year|month|week|day)[-–]?old)/i);
    if (!agePt) return null;

    const labsMatch = plain.match(/(lab(?:oratory)?\s*(?:results?|findings?|values?|studies?)|(?:vitals?|vital signs?|physical examination))[:\s]/i);
    const stemMatch = plain.match(/\s(which of the following|what is the most (?:likely|appropriate|effective)|the most likely|what should|the next best step)/i);

    if (!labsMatch && !stemMatch) return null;

    const labStart = labsMatch?.index ?? plain.length;
    const stemStart = stemMatch?.index ?? plain.length;
    const divider1 = Math.min(labStart, stemStart);

    const sections = [];
    sections.push({ type: 'vignette', text: plain.slice(0, divider1).trim() });

    if (labsMatch && labStart < stemStart) {
        sections.push({ type: 'labs', text: plain.slice(labStart, stemStart).trim() });
    }

    const questionText = plain.slice(Math.min(stemStart, plain.length)).trim();
    if (questionText) sections.push({ type: 'stem', text: questionText });

    return sections.length > 1 ? sections : null;
}

// Server-driven exam runner.
//
// The backend owns generation, shuffle, and grading. This component:
//   1. starts an attempt on mount (server-fixed question + option order),
//   2. submits each answer to the server (no local isCorrect reads),
//   3. paints from the answer response (explanationMode "now") or the finish answer key
//      (explanationMode "later" reveals nothing until finish),
//   4. reads the final score + answer key from finish.
//
// `exam` is the sanitized exam from POST /api/exams (carries examId; options have no isCorrect).
const ExamViewer = ({ exam, onClose, onCreateFlaggedFolder, onSaveAndRecord, onIncorrectAnswers, onViewSourceCard }) => {
    const examId = exam?.examId || exam?.id;
    const explanationMode = exam?.config?.explanationMode === 'later' ? 'later' : 'now';
    const timedMode = !!exam?.config?.timedMode;

    const [attempt, setAttempt] = useState(null);
    const [attemptError, setAttemptError] = useState('');
    const [gameState, setGameState] = useState('testing'); // 'testing' | 'results' | 'reviewing'
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [flagged, setFlagged] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [finishResult, setFinishResult] = useState(null);
    const [showExplanationModal, setShowExplanationModal] = useState(false);

    // { [questionId]: 'picking' | 'submitting' | 'done' | 'error' }
    const [reportState, setReportState] = useState({});
    // { [questionId]: { creditsAwarded, limitReason } }
    const [reportResults, setReportResults] = useState({});
    const [sessionCreditsEarned, setSessionCreditsEarned] = useState(0);

    // Timer state
    const [secondsLeft, setSecondsLeft] = useState(SECONDS_PER_QUESTION);
    const [timeExpired, setTimeExpired] = useState(false);
    const timerRef = useRef(null);

    // Start an attempt when the exam changes.
    useEffect(() => {
        let cancelled = false;
        setAttempt(null);
        setAttemptError('');
        setGameState('testing');
        setCurrentIndex(0);
        setUserAnswers({});
        setFlagged({});
        setFinishResult(null);
        setShowExplanationModal(false);

        if (!examId) {
            setAttemptError('This exam can no longer be taken (missing exam id). Create a new exam to continue.');
            return () => { cancelled = true; };
        }

        (async () => {
            try {
                const data = await startAttempt(examId);
                if (cancelled) return;
                const questions = Array.isArray(data?.sanitizedQuestions) ? data.sanitizedQuestions : [];
                setAttempt({ attemptId: data?.attemptId, questions });
                try { trackEvent(Events.exam_begin, { examId, title: exam?.title, questions: questions.length }); } catch (_) {}
            } catch (e) {
                if (cancelled) return;
                console.error('[ExamViewer] startAttempt failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
                setAttemptError(getApiErrorMessage(e, 'Could not start this exam.'));
            }
        })();

        return () => { cancelled = true; };
    }, [examId]);

    // Timer: reset and start on each question change
    useEffect(() => {
        if (!timedMode || gameState !== 'testing') {
            clearInterval(timerRef.current);
            return;
        }
        setSecondsLeft(SECONDS_PER_QUESTION);
        setTimeExpired(false);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setTimeExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [currentIndex, timedMode, gameState]);

    const questions = Array.isArray(attempt?.questions) ? attempt.questions : [];
    const currentQuestion = questions[currentIndex];
    const flaggedCount = Object.values(flagged).filter(Boolean).length;

    // Auto-advance when time expires on an unanswered question
    useEffect(() => {
        if (!timeExpired || gameState !== 'testing') return;
        const isAnswered = currentQuestion && userAnswers[currentQuestion.id] !== undefined;
        if (!isAnswered) handleNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeExpired]);

    const handleToggleFlag = () => {
        const qid = currentQuestion?.id;
        if (!qid) return;
        setFlagged(prev => ({ ...prev, [qid]: !prev[qid] }));
    };

    const handleOpenReport = (qid) => {
        if (reportState[qid]) return;
        setReportState(prev => ({ ...prev, [qid]: 'picking' }));
    };

    const handleCancelReport = (qid) => {
        setReportState(prev => { const next = { ...prev }; delete next[qid]; return next; });
    };

    const handleSubmitReport = async (qid, reason) => {
        setReportState(prev => ({ ...prev, [qid]: 'submitting' }));
        try {
            const result = await reportQuestion(examId, qid, { reason });
            setReportState(prev => ({ ...prev, [qid]: 'done' }));
            setReportResults(prev => ({ ...prev, [qid]: { creditsAwarded: result?.creditsAwarded ?? 0, limitReason: result?.limitReason ?? null } }));
            if (result?.creditsAwarded > 0) {
                setSessionCreditsEarned(prev => prev + result.creditsAwarded);
            }
        } catch (e) {
            console.error('[ExamViewer] reportQuestion failed:', e);
            setReportState(prev => ({ ...prev, [qid]: 'error' }));
        }
    };

    const handleAnswerSelect = async (option) => {
        const qid = currentQuestion?.id;
        if (!qid || gameState !== 'testing' || userAnswers[qid] || submitting) return;
        clearInterval(timerRef.current);
        setUserAnswers(prev => ({ ...prev, [qid]: { optionId: option.id } }));
        setSubmitting(true);
        try {
            const data = await submitAnswer(examId, attempt.attemptId, { questionId: qid, optionId: option.id });
            if (explanationMode === 'now') {
                setUserAnswers(prev => ({
                    ...prev,
                    [qid]: {
                        optionId: option.id,
                        isCorrect: !!data?.isCorrect,
                        correctOptionId: data?.correctOptionId || null,
                        explanations: data?.explanations || {},
                        explanation: data?.explanation || '',
                        teachingPoints: data?.teachingPoints || [],
                        references: data?.references || [],
                    },
                }));
                setShowExplanationModal(true);
            }
        } catch (e) {
            if (e?.code !== 'ALREADY_ANSWERED') {
                console.error('[ExamViewer] submitAnswer failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
                setUserAnswers(prev => { const next = { ...prev }; delete next[qid]; return next; });
                setAttemptError(getApiErrorMessage(e, 'Could not submit your answer.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleNext = async () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            return;
        }
        if (gameState === 'reviewing') {
            setGameState('results');
            return;
        }
        setSubmitting(true);
        try {
            const flaggedIds = Object.keys(flagged).filter(id => flagged[id]);
            const result = await finishAttempt(examId, attempt.attemptId, { flagged: flaggedIds });
            setFinishResult(result);
            setGameState('results');
            try {
                trackEvent(Events.exam_complete, {
                    examId, title: exam?.title,
                    score: result?.score, totalQuestions: result?.totalQuestions, percentage: result?.percentage,
                });
            } catch (_) {}
        } catch (e) {
            console.error('[ExamViewer] finishAttempt failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            setAttemptError(getApiErrorMessage(e, 'Could not finish the exam.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReview = () => {
        setCurrentIndex(0);
        setGameState('reviewing');
    };

    const answerKeyById = React.useMemo(() => {
        const map = {};
        for (const entry of (finishResult?.answerKey || [])) map[entry.questionId] = entry;
        return map;
    }, [finishResult]);

    // Once, right when results land: auto-flag the source card behind every wrong question
    // (persists to the card's own folder, feeds the "flagged cards only" exam filter) AND
    // auto-set the exam-session flag on wrong questions (feeds "Study Flagged" below, so it
    // includes everything you missed without requiring you to have manually flagged it
    // during the exam). Covers both explanationMode "now" and "later" from one hook point —
    // isCorrect isn't known per-answer in "later" mode until the answer key arrives here.
    const autoFlaggedRef = useRef(false);
    useEffect(() => {
        if (gameState !== 'results' || autoFlaggedRef.current) return;
        autoFlaggedRef.current = true;
        const wrongCardIds = new Set();
        const wrongQuestionIds = [];
        for (const q of questions) {
            const ua = userAnswers[q.id];
            const key = answerKeyById[q.id];
            const isCorrect = ua?.isCorrect ?? (key && ua && ua.optionId === key.correctOptionId) ?? null;
            if (isCorrect === false) {
                wrongQuestionIds.push(q.id);
                (key?.sourceCardIds || []).forEach((id) => wrongCardIds.add(String(id)));
            }
        }
        if (wrongQuestionIds.length > 0) {
            setFlagged((prev) => {
                const next = { ...prev };
                for (const qid of wrongQuestionIds) next[qid] = true;
                return next;
            });
        }
        if (wrongCardIds.size > 0) onIncorrectAnswers?.([...wrongCardIds]);
    }, [gameState, questions, userAnswers, answerKeyById, onIncorrectAnswers]);

    const buildFlaggedItems = () => {
        const items = [];
        for (const q of questions) {
            if (!flagged[q.id]) continue;
            const key = answerKeyById[q.id];
            const correct = key ? (key.options || []).find(o => o.id === key.correctOptionId) : null;
            items.push({
                questionText: q.questionText,
                sourceCardIds: (key?.sourceCardIds || []).map(String),
                answerText: correct?.text || '',
            });
        }
        return items;
    };

    // --- Error / loading gates ---
    if (attemptError) {
        return (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
                <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-slate-900/90 p-6 shadow-2xl shadow-black/40 text-center">
                    <p className="text-slate-200 text-sm">{attemptError}</p>
                    <button className="mt-5 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10" onClick={onClose}>Close</button>
                </div>
            </div>
        );
    }

    if (!attempt || questions.length === 0) {
        return (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/40">
                    <span className="h-5 w-5 rounded-full border-2 border-teal-400 border-t-transparent animate-spin"></span>
                    <span className="text-slate-200 text-sm">Preparing your exam...</span>
                </div>
            </div>
        );
    }

    if (gameState === 'results') {
        const score = finishResult?.score ?? 0;
        const totalQuestions = finishResult?.totalQuestions ?? questions.length;
        const percentage = finishResult?.percentage ?? 0;
        const PASS_THRESHOLD = 70;
        const passed = percentage >= PASS_THRESHOLD;

        const scoreBand = percentage >= 85 ? { label: 'High Pass', color: 'text-emerald-300', ring: '#34d399' }
            : percentage >= PASS_THRESHOLD ? { label: 'Pass', color: 'text-sky-300', ring: '#38bdf8' }
            : percentage >= 60 ? { label: 'Borderline', color: 'text-amber-300', ring: '#fbbf24' }
            : { label: 'Fail', color: 'text-rose-400', ring: '#f87171' };

        const R = 52, C = 2 * Math.PI * R;
        const offset = C * (1 - percentage / 100);

        const breakdown = questions.map((q, i) => {
            const ua = userAnswers[q.id];
            const key = answerKeyById[q.id];
            const isFlagged = !!flagged[q.id];
            const isCorrect = ua?.isCorrect ?? (key && ua && ua.optionId === key.correctOptionId) ?? null;
            const sourceCardIds = (key?.sourceCardIds || []).map(String);
            return { index: i + 1, questionText: q.questionText, isCorrect, isFlagged, answered: !!ua, sourceCardIds };
        });

        const correct = breakdown.filter(r => r.isCorrect).length;
        const incorrect = breakdown.filter(r => r.isCorrect === false).length;
        const unanswered = breakdown.filter(r => !r.answered).length;

        return (
            <div className="fixed inset-0 z-40 flex flex-col bg-slate-950/80 backdrop-blur-sm px-3 py-4 sm:px-4 sm:py-6 overflow-y-auto">
                <div className="mx-auto w-full max-w-xl rounded-2xl border border-white/10 bg-[#0d1526]/97 shadow-2xl shadow-black/50 overflow-hidden">
                    <div className="flex flex-col sm:flex-row items-center gap-6 p-6 border-b border-white/8">
                        <div className="relative shrink-0">
                            <svg width="128" height="128" className="-rotate-90">
                                <circle cx="64" cy="64" r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                                <circle
                                    cx="64" cy="64" r={R}
                                    fill="none"
                                    stroke="rgba(255,255,255,0.15)"
                                    strokeWidth="10"
                                    strokeDasharray={`2 ${C - 2}`}
                                    strokeDashoffset={-(C * (1 - PASS_THRESHOLD / 100))}
                                    strokeLinecap="round"
                                />
                                <circle
                                    cx="64" cy="64" r={R}
                                    fill="none"
                                    stroke={scoreBand.ring}
                                    strokeWidth="10"
                                    strokeDasharray={C}
                                    strokeDashoffset={offset}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)', filter: `drop-shadow(0 0 6px ${scoreBand.ring}60)` }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className={`text-2xl font-bold tabular-nums leading-none ${scoreBand.color}`}>{percentage}%</span>
                                <span className={`text-[11px] font-semibold mt-1 ${scoreBand.color}`}>{scoreBand.label}</span>
                            </div>
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                            <h2 className="text-lg font-bold text-white tracking-tight">Exam Complete</h2>
                            <p className="text-sm text-slate-400 mt-1">{exam?.title || 'Flash Exam'}{timedMode && <span className="ml-2 text-[11px] text-amber-400/80">⏱ Timed</span>}</p>
                            <div className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${passed ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25' : 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-400/25'}`}>
                                {passed ? (
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                )}
                                {passed ? `Passing (≥${PASS_THRESHOLD}%)` : `Below passing (${PASS_THRESHOLD}%)`}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-3 justify-center sm:justify-start text-sm">
                                <div className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                                    <span className="text-slate-300">{correct} correct</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-rose-400 shrink-0" />
                                    <span className="text-slate-300">{incorrect} incorrect</span>
                                </div>
                                {flaggedCount > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                                        <span className="text-slate-300">{flaggedCount} flagged</span>
                                    </div>
                                )}
                                {unanswered > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-2 w-2 rounded-full bg-slate-500 shrink-0" />
                                        <span className="text-slate-300">{unanswered} skipped</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="px-5 py-4 border-b border-white/8">
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Question Breakdown</p>
                        <div className="grid gap-1.5 max-h-48 overflow-y-auto no-scrollbar">
                            {breakdown.map(({ index, questionText, isCorrect, isFlagged, sourceCardIds }) => (
                                <div key={index} className="flex items-start gap-2.5 rounded-lg px-3 py-2 bg-slate-900/50">
                                    <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                                        isCorrect === true ? 'bg-emerald-500/20 text-emerald-300'
                                        : isCorrect === false ? 'bg-rose-500/20 text-rose-300'
                                        : 'bg-slate-700/50 text-slate-500'
                                    }`}>
                                        {isCorrect === true ? '✓' : isCorrect === false ? '✕' : '–'}
                                    </div>
                                    <span className="text-xs text-slate-400 leading-relaxed line-clamp-1 flex-1">{index}. {questionText?.replace(/<[^>]+>/g,'').slice(0,100)}</span>
                                    {isFlagged && <span className="shrink-0 text-[10px] text-amber-400">⚑</span>}
                                    {isCorrect === false && sourceCardIds.length > 0 && onViewSourceCard && (
                                        <button
                                            type="button"
                                            onClick={() => onViewSourceCard(sourceCardIds)}
                                            className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-teal-300 bg-teal-500/10 hover:bg-teal-500/20 transition"
                                            title="View the original flashcard this question came from"
                                        >
                                            <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                                            Card
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {sessionCreditsEarned > 0 && (
                        <div className="flex items-center gap-2 px-5 py-3 bg-emerald-500/10 border-b border-white/8">
                            <svg className="h-4 w-4 shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/></svg>
                            <span className="text-xs text-emerald-300">
                                Thanks for your reports! <span className="font-semibold">+{sessionCreditsEarned} credits</span> added to your account.
                            </span>
                        </div>
                    )}
                    <div className="px-5 py-4 flex flex-col sm:flex-row gap-2">
                        <button className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 shadow-md shadow-teal-500/20 transition" onClick={handleReview}>
                            Review Answers
                        </button>
                        <button className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 transition" onClick={() => onSaveAndRecord?.(exam, score)}>
                            Save Score
                        </button>
                        {flaggedCount > 0 && (
                            <button className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 bg-amber-300 hover:bg-amber-200 transition" onClick={() => onCreateFlaggedFolder?.(buildFlaggedItems())}>
                                Study Flagged ({flaggedCount})
                            </button>
                        )}
                        <button className="sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const reviewing = gameState === 'reviewing';
    const currentAnswer = userAnswers[currentQuestion.id];
    const isAnswered = currentAnswer !== undefined;
    const reviewKey = reviewing ? answerKeyById[currentQuestion.id] : null;

    const correctOptionId = reviewing ? (reviewKey?.correctOptionId || null) : (currentAnswer?.correctOptionId || null);
    const selectedOptionId = currentAnswer?.optionId || null;

    // Parse stem vs question from questionText (normalizer stores as `${stem}\n\n${question}`)
    const qt = currentQuestion.questionText || '';
    const lastBreak = qt.lastIndexOf('\n\n');
    const displayStem = lastBreak > 0 ? qt.slice(0, lastBreak) : qt;
    const displayQuestion = lastBreak > 0 ? qt.slice(lastBreak + 2) : '';

    // Derive explanation content from either live answer or review key
    const displayExplanations = reviewing
        ? Object.fromEntries((reviewKey?.options || []).map(o => [o.id, o.explanation || '']))
        : (currentAnswer?.explanations || {});
    const displayExplanation = reviewing ? (reviewKey?.explanation || '') : (currentAnswer?.explanation || '');
    const displayTeachingPoints = reviewing ? (reviewKey?.teachingPoints || []) : (currentAnswer?.teachingPoints || []);
    const displayReferences = normalizeReferences(reviewing ? reviewKey?.references : currentAnswer?.references);

    const showInlineExplanation = isAnswered || reviewing;

    return (
        <div className="fixed inset-0 z-40 flex flex-col bg-slate-950/85 backdrop-blur-sm px-3 sm:px-4 py-4 sm:py-6">
            <div className="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d1626] shadow-2xl shadow-black/50 sm:max-w-2xl">

                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-white/8 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/25 bg-teal-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-teal-400">
                            {reviewing ? 'Review Mode' : (exam?.config?.examType ? exam.config.examType.toUpperCase() : 'Flash Exam')}
                        </span>
                        <span className="text-xs text-slate-500 tabular-nums">Q {currentIndex + 1}/{questions.length}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {timedMode && gameState === 'testing' && (
                            <div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-mono font-bold tabular-nums transition ${
                                secondsLeft <= 15 ? 'bg-rose-500/20 text-rose-300 animate-pulse'
                                : secondsLeft <= 30 ? 'bg-amber-500/20 text-amber-300'
                                : 'bg-slate-800/60 text-slate-400'
                            }`}>
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}
                            </div>
                        )}
                        <button
                            onClick={handleToggleFlag}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-white/10 transition ${flagged[currentQuestion.id] ? 'bg-amber-300 text-slate-900 ring-amber-300' : 'bg-white/5 text-slate-400 hover:text-white'}`}
                        >
                            ⚑ {flagged[currentQuestion.id] ? 'Flagged' : 'Flag'}
                        </button>
                        <button onClick={onClose} className="rounded-full p-1.5 text-slate-500 hover:text-white hover:bg-white/8 transition">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                </div>

                {/* Time bar */}
                {timedMode && gameState === 'testing' && (
                    <div className="h-0.5 w-full bg-white/5 shrink-0">
                        <div
                            className={`h-0.5 transition-all duration-1000 ${secondsLeft <= 15 ? 'bg-rose-400' : secondsLeft <= 30 ? 'bg-amber-400' : 'bg-teal-400'}`}
                            style={{ width: `${(secondsLeft / SECONDS_PER_QUESTION) * 100}%` }}
                        />
                    </div>
                )}

                {/* Question area */}
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {Number(exam?.config?.rejectedSlots) > 0 && (
                        <p className="px-5 pt-3 text-xs text-amber-300/80">
                            {questions.length} of {exam?.config?.requestedCount || questions.length} questions generated.
                        </p>
                    )}

                    {/* Clinical Vignette */}
                    <div className="px-5 pt-5 pb-4 border-b border-white/6">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-2.5">Clinical Vignette</p>
                        <div
                            className="prose prose-invert prose-sm max-w-none text-slate-200 leading-relaxed [&_table]:w-full [&_table]:text-xs [&_th]:text-left [&_th]:text-slate-400 [&_th]:font-semibold [&_th]:pb-1 [&_td]:py-1 [&_td]:pr-4 [&_td]:text-slate-300 [&_tr]:border-b [&_tr]:border-white/6"
                            dangerouslySetInnerHTML={{ __html: marked(displayStem) }}
                        />
                    </div>

                    {/* Question stem */}
                    {displayQuestion && (
                        <div className="px-5 py-4 border-b border-white/6">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70 mb-2">Question</p>
                            <p className="text-sm font-semibold text-white border-l-2 border-teal-400/60 pl-3 leading-relaxed">{displayQuestion}</p>
                        </div>
                    )}

                    {/* Answer options */}
                    <div className="px-5 py-4 grid gap-2">
                        {!isAnswered && !reviewing && (
                            <p className="text-[11px] text-slate-600 mb-1">Select an answer</p>
                        )}
                        {currentQuestion.options.map((option, index) => {
                            const choiceLetter = String.fromCharCode(65 + index);
                            const isSelected = selectedOptionId === option.id;
                            const isThisCorrect = (isAnswered || reviewing) && option.id === correctOptionId;
                            const isThisWrong = (isAnswered || reviewing) && isSelected && !isThisCorrect;
                            const isOther = (isAnswered || reviewing) && !isThisCorrect && !isThisWrong;

                            let rowStyle = 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-teal-400/40 hover:bg-teal-500/5 cursor-pointer';
                            let letterStyle = 'bg-white/6 text-slate-400';
                            if (isThisCorrect) {
                                rowStyle = 'border-emerald-400/60 bg-emerald-500/10 text-emerald-200 cursor-default';
                                letterStyle = 'bg-emerald-500/30 text-emerald-300';
                            } else if (isThisWrong) {
                                rowStyle = 'border-red-400/50 bg-red-500/8 text-red-200 cursor-default';
                                letterStyle = 'bg-red-500/25 text-red-300';
                            } else if (isOther) {
                                rowStyle = 'border-white/5 bg-transparent text-slate-500 cursor-default';
                                letterStyle = 'bg-white/4 text-slate-600';
                            }

                            return (
                                <button
                                    key={option.id}
                                    className={`w-full text-left flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition ${rowStyle}`}
                                    onClick={() => handleAnswerSelect(option)}
                                    disabled={isAnswered || reviewing || submitting}
                                >
                                    <span className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${letterStyle}`}>
                                        {choiceLetter}
                                    </span>
                                    <span className="flex-1 leading-snug">{option.text}</span>
                                    {isThisCorrect && <svg className="ml-auto h-5 w-5 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>}
                                    {isThisWrong && <svg className="ml-auto h-5 w-5 text-red-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/></svg>}
                                </button>
                            );
                        })}
                        {!isAnswered && !reviewing && (
                            <p className="text-[10px] text-slate-700 text-center mt-1">Tap to select</p>
                        )}
                    </div>

                    {/* Inline explanation — shown after answering, matching landing page style */}
                    {showInlineExplanation && (
                        <div className="border-t border-white/6 divide-y divide-white/6">

                            {/* Verdict */}
                            {(() => {
                                const wasCorrect = reviewing
                                    ? (selectedOptionId && correctOptionId && selectedOptionId === correctOptionId)
                                    : !!currentAnswer?.isCorrect;
                                return (
                                    <div className={`flex items-center gap-3 px-5 py-3 ${wasCorrect ? 'bg-emerald-500/8' : 'bg-rose-500/8'}`}>
                                        <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${wasCorrect ? 'bg-emerald-500/30' : 'bg-rose-500/30'}`}>
                                            {wasCorrect ? (
                                                <svg className="h-3.5 w-3.5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                                            ) : (
                                                <svg className="h-3.5 w-3.5 text-rose-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/></svg>
                                            )}
                                        </div>
                                        <p className={`text-sm font-bold ${wasCorrect ? 'text-emerald-300' : 'text-rose-300'}`}>
                                            {wasCorrect ? 'Correct!' : (selectedOptionId ? 'Incorrect' : 'Not answered')}
                                        </p>
                                    </div>
                                );
                            })()}

                            {/* Overall explanation */}
                            {displayExplanation && (
                                <div className="px-5 py-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Explanation</p>
                                    <p className="text-sm text-slate-200 leading-relaxed">{displayExplanation}</p>
                                </div>
                            )}

                            {/* Per-choice rationales */}
                            {currentQuestion.options.length > 0 && Object.keys(displayExplanations).length > 0 && (
                                <div className="px-5 py-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">Rationales</p>
                                    <div className="space-y-2">
                                        {currentQuestion.options.map((option, index) => {
                                            const letter = String.fromCharCode(65 + index);
                                            const isThisCorrect = option.id === correctOptionId;
                                            const isThisSelected = option.id === selectedOptionId;
                                            const rationale = displayExplanations[option.id] || '';
                                            let rowStyle = 'border-white/6 bg-white/[0.02]';
                                            let letterStyle = 'bg-white/8 text-slate-400';
                                            let textStyle = 'text-slate-400';
                                            let icon = null;
                                            if (isThisCorrect) {
                                                rowStyle = 'border-emerald-500/30 bg-emerald-500/8';
                                                letterStyle = 'bg-emerald-500/30 text-emerald-300';
                                                textStyle = 'text-emerald-200';
                                                icon = <svg className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>;
                                            } else if (isThisSelected) {
                                                rowStyle = 'border-rose-500/30 bg-rose-500/6';
                                                letterStyle = 'bg-rose-500/25 text-rose-300';
                                                textStyle = 'text-rose-200';
                                                icon = <svg className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/></svg>;
                                            }
                                            return (
                                                <div key={option.id} className={`rounded-xl border px-4 py-3 ${rowStyle}`}>
                                                    <div className="flex items-start gap-3">
                                                        <span className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${letterStyle}`}>{letter}</span>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-xs font-semibold mb-1 ${textStyle}`}>{option.text}</p>
                                                            {rationale && <p className="text-xs text-slate-400 leading-relaxed">{rationale}</p>}
                                                        </div>
                                                        {icon}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Teaching Points */}
                            {displayTeachingPoints.length > 0 && (
                                <div className="px-5 py-4">
                                    <div className="rounded-xl border border-teal-500/25 bg-teal-500/6 px-4 py-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="h-4 w-4 text-teal-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                            <p className="text-[11px] font-bold uppercase tracking-widest text-teal-400">Teaching Points</p>
                                        </div>
                                        <ul className="space-y-2">
                                            {displayTeachingPoints.map((point, i) => (
                                                <li key={i} className="flex items-start gap-2.5 text-xs text-teal-200/90 leading-relaxed">
                                                    <span className="shrink-0 mt-1 h-1.5 w-1.5 rounded-full bg-teal-400" />
                                                    {point}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* References */}
                            {displayReferences.length > 0 && (
                                <div className="px-5 py-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">References</p>
                                    <ul className="space-y-1.5">
                                        {displayReferences.map((ref, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                                                <span className="shrink-0 mt-1.5 h-1 w-1 rounded-full bg-slate-700" />
                                                {ref}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Report question */}
                            {isAnswered && (() => {
                                const qid = currentQuestion.id;
                                const rs = reportState[qid];
                                const REASONS = [
                                    { key: 'incorrect_answer', label: 'Wrong answer key' },
                                    { key: 'ambiguous', label: 'Ambiguous / multiple valid' },
                                    { key: 'factual_error', label: 'Factual error in stem' },
                                    { key: 'mismatch', label: 'Question–answer mismatch' },
                                    { key: 'other', label: 'Other' },
                                ];
                                if (rs === 'done') {
                                    const rr = reportResults[qid] || {};
                                    const limitMsg = rr.limitReason === 'limit_reached' ? 'Monthly credit limit reached.' : null;
                                    return (
                                        <div className={`mx-5 mb-4 flex items-center gap-2 rounded-xl px-4 py-2.5 ${limitMsg ? 'bg-slate-800/60 ring-1 ring-white/10' : 'bg-emerald-500/10 ring-1 ring-emerald-400/20'}`}>
                                            <svg className={`h-4 w-4 shrink-0 ${limitMsg ? 'text-slate-400' : 'text-emerald-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                                            <span className={`text-xs font-medium ${limitMsg ? 'text-slate-300' : 'text-emerald-300'}`}>{limitMsg || 'Report received — question removed.'}</span>
                                            {rr.creditsAwarded > 0 && <span className="ml-auto text-xs font-semibold text-emerald-200 bg-emerald-500/20 rounded-full px-2 py-0.5">+{rr.creditsAwarded} credits</span>}
                                        </div>
                                    );
                                }
                                if (rs === 'error') return (
                                    <div className="mx-5 mb-4 flex items-center gap-2 rounded-xl bg-rose-500/10 ring-1 ring-rose-400/20 px-4 py-2.5">
                                        <span className="text-xs text-rose-300">Could not submit report. Try again later.</span>
                                        <button className="ml-auto text-xs text-rose-400 underline" onClick={() => handleCancelReport(qid)}>Dismiss</button>
                                    </div>
                                );
                                if (rs === 'picking') return (
                                    <div className="mx-5 mb-4 rounded-xl border border-white/10 bg-white/3 px-4 py-3">
                                        <p className="text-xs font-semibold text-slate-300 mb-2.5">What's wrong with this question?</p>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {REASONS.map(r => (
                                                <button key={r.key} onClick={() => handleSubmitReport(qid, r.key)} className="rounded-full border border-white/10 bg-slate-800/70 px-3 py-1 text-xs text-slate-200 hover:border-teal-400/50 hover:text-white transition">{r.label}</button>
                                            ))}
                                        </div>
                                        <button onClick={() => handleCancelReport(qid)} className="text-[11px] text-slate-500 hover:text-slate-300 transition">Cancel</button>
                                    </div>
                                );
                                if (rs === 'submitting') return (
                                    <div className="mx-5 mb-4 flex items-center gap-2 px-4 py-2.5">
                                        <span className="h-4 w-4 rounded-full border-2 border-teal-400 border-t-transparent animate-spin shrink-0" />
                                        <span className="text-xs text-slate-400">Submitting report…</span>
                                    </div>
                                );
                                return (
                                    <div className="px-5 pb-5">
                                        <button onClick={() => handleOpenReport(qid)} className="w-full flex items-center justify-between gap-3 rounded-xl bg-white/3 ring-1 ring-white/8 hover:bg-white/5 hover:ring-amber-400/30 px-4 py-3 transition group">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="h-7 w-7 shrink-0 rounded-lg bg-amber-500/15 group-hover:bg-amber-500/25 flex items-center justify-center transition">
                                                    <svg className="h-3.5 w-3.5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3C6.477 3 2 7.477 2 12s4.477 9 10 9 10-4.477 10-9S17.523 3 12 3z"/></svg>
                                                </div>
                                                <div className="text-left min-w-0">
                                                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition">Don't agree? Flag for review</p>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">1 credit applied · up to 15/month</p>
                                                </div>
                                            </div>
                                            <svg className="h-4 w-4 shrink-0 text-slate-600 group-hover:text-amber-400 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {!showInlineExplanation && (
                        <div className="px-5 pb-3 text-center">
                            <p className="text-[11px] text-slate-700">Select an answer above</p>
                        </div>
                    )}
                </div>

                {/* Footer nav */}
                {showInlineExplanation && (
                    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-white/8 px-4 py-3">
                        <p className="text-xs text-slate-600 tabular-nums">{currentIndex + 1} of {questions.length}</p>
                        <button
                            className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 disabled:opacity-60 transition shadow-lg shadow-teal-500/20"
                            onClick={handleNext}
                            disabled={submitting}
                        >
                            {currentIndex < questions.length - 1 ? 'Next Question' : (gameState === 'testing' ? 'Finish Exam' : 'Finish Review')}
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ExamViewer;
